import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { AUTHENTICATION_STATE_CLEARED_EVENT } from '../api/client';
import { getEventIdFromSearch } from '../routes/appRoutes';

const ID_KEY = 'selectedEventId';
const NAME_KEY = 'selectedEventName';

type EventContextValue = {
  /** 현재 선택된 대회 id (없으면 null) */
  selectedEventId: number | null;
  /** 현재 선택된 대회 이름 (헤더 표시용, 없으면 null) */
  selectedEventName: string | null;
  setSelectedEventId: (id: number | null, name?: string | null) => void;
  setSelectedEventName: (name: string | null) => void;
};

const EventContext = createContext<EventContextValue | undefined>(undefined);

type EventProviderProps = Readonly<{
  children: ReactNode;
}>;

// 새로고침에도 유지되도록 sessionStorage 에서 초기값을 읽음
function readInitialId(): number | null {
  const urlEventId = getEventIdFromSearch(globalThis.location.search);
  if (urlEventId != null) return urlEventId;

  const raw = sessionStorage.getItem(ID_KEY);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? null : parsed;
}

export function EventProvider({ children }: EventProviderProps) {
  const location = useLocation();
  const [selectedEventId, setSelectedEventId] = useState<number | null>(readInitialId);
  const [selectedEventName, setSelectedEventName] = useState<string | null>(
    () => sessionStorage.getItem(NAME_KEY)
  );

  const updateSelectedEventName = useCallback((name: string | null) => {
    setSelectedEventName(name);
    if (name == null) {
      sessionStorage.removeItem(NAME_KEY);
    } else {
      sessionStorage.setItem(NAME_KEY, name);
    }
  }, []);

  const updateSelectedEventId = useCallback(
    (id: number | null, name?: string | null) => {
      setSelectedEventId(id);
      if (id == null) {
        sessionStorage.removeItem(ID_KEY);
        updateSelectedEventName(null);
      } else {
        sessionStorage.setItem(ID_KEY, String(id));
      }
      // 이름이 함께 전달되면 같이 갱신 (id 만 바뀔 땐 건드리지 않음)
      if (name !== undefined) {
        updateSelectedEventName(name);
      }
    },
    [updateSelectedEventName]
  );

  useEffect(() => {
    const urlEventId = getEventIdFromSearch(location.search);
    if (urlEventId == null) return;

    updateSelectedEventId(urlEventId);
  }, [location.search, updateSelectedEventId]);

  useEffect(() => {
    const clearSelectedEvent = () => {
      setSelectedEventId(null);
      setSelectedEventName(null);
    };

    globalThis.addEventListener(AUTHENTICATION_STATE_CLEARED_EVENT, clearSelectedEvent);
    return () => globalThis.removeEventListener(AUTHENTICATION_STATE_CLEARED_EVENT, clearSelectedEvent);
  }, []);

  const value = useMemo(
    () => ({
      selectedEventId,
      selectedEventName,
      setSelectedEventId: updateSelectedEventId,
      setSelectedEventName: updateSelectedEventName,
    }),
    [selectedEventId, selectedEventName, updateSelectedEventId, updateSelectedEventName]
  );

  return (
    <EventContext.Provider value={value}>
      {children}
    </EventContext.Provider>
  );
}

export function useEventContext() {
  const ctx = useContext(EventContext);
  if (!ctx) {
    throw new Error('useEventContext 는 EventProvider 안에서만 사용할 수 있습니다.');
  }
  return ctx;
}
